/**
 * The multi-level bill of materials on an Item: the explosion down, or the where-used up -- and,
 * for a reader who may edit the item, the editor of its own lines.
 *
 * ED, 2026-10-30: "Build the multi-level BOM explosion and where-used." Both are walks over the
 * Bill of Materials edges (/t1/v1/bom_explosion, /t1/v1/bom_where_used), each node read under the
 * reader's own sight: an unseen node is its ID, grey, no link, and nothing below (or above) it is
 * shown. The explosion shows, indented by level, each line's quantity per one parent and the
 * EXTENDED quantity -- what one of this item needs of that node -- with the pinned or current
 * revision, the reference designator and the standard cost; a configuration of this item's bill may
 * be chosen (the default is taken otherwise). The where-used shows what contains this item, level by
 * level, with how many of it go into one of each parent. The ROLL-UP (2026-10-30, units convert
 * through a base unit per dimension) is the buyer's question of the explosion: how much of each
 * component one of this item needs over every path, in the component's dimension's base unit, with a
 * component whose unit does not convert (a set) said so rather than left out.
 *
 * THE EDITOR (Ed, 2026-10-31: "Build a BOM editor on the Item pane"). A line is an edge of the Bill of
 * Materials relationship type, written through the same door the Related Objects grid uses
 * (/t1/v1/object_relationships, v1.ObjectRelationship-001), so every rule the service holds applies
 * here unchanged: two Items only, never itself, never a cycle, a quantity above zero in a unit the
 * tenant knows, a configuration of this parent and not another's, and the change-control finding
 * or refusal (CC-OBRL-UNCTL) when the parent is released and no open change order names it. ONLY
 * THIS ITEM'S OWN LINES ARE EDITABLE HERE -- the level-1 lines of the explosion. A deeper line
 * belongs to the component's bill and is edited on that item, which is also where the privilege for
 * it is held. The controls are drawn only for a reader holding the item's edit (the server refuses
 * regardless) and never on a superseded, void or cancelled item, whose edges are frozen. An update
 * sends the WHOLE line, null for a field blanked, so a cleared designator clears and an unpinned
 * line floats again; the service test of the same name proves that contract.
 */
import React, {useCallback, useContext, useEffect, useMemo, useState} from "react";
import {useNavigate} from "react-router-dom";
import {Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, MenuItem, TextField,
        Typography} from "@mui/material";
import {AppContext} from "../../util/AppContext";
import {makeCresAxios} from "../../util/Axios";
import {Blue3, Grey2, Grey6, Grey7, Grey8, Grey17} from "../../theme/styles";
import {OBJECT_CLASSIFICATIONS} from "../../util/objectClassifications";
import ObjectList, {PickedRecord} from "../../pages/ObjectList";

const EDGE_TYPE = "Bill of Materials";
const RELATIONSHIP_IDOC = "v1.ObjectRelationship-001";
/** The statuses whose edges the service freezes (object_relationship._FROZEN_STATUSES). */
const FROZEN = ["SUPERSEDED", "VOID", "CANCELLED"];

type Line = {Level: number; Path: string[]; ParentObjectUSID?: string; EdgeUSID?: string; ObjectUSID: string; RevisionUSID?: string;
             ObjectID?: string | null; Name?: string | null;
             Revision?: string | null; Status?: string | null; Visible: boolean; Pinned?: boolean; Quantity?: any; UnitOfMeasure?: string | null;
             ExtendedQuantity?: any; ReferenceDesignator?: string | null; StandardCost?: any; StandardCostCurrency?: string | null;
             ExtendedStandardCost?: any; Configuration?: string | null; ConfigurationUSID?: string | null; Cycle?: boolean; HasChildren?: boolean;
             Dimension?: string | null; BaseUnit?: string | null; BaseQuantity?: any;
             Substitute?: boolean; SubstituteForEdgeUSID?: string | null};
type Rolled = {ObjectUSID: string; ObjectID?: string | null; Name?: string | null; Dimension?: string | null; UnitOfMeasure?: string | null;
               Quantity?: any; Lines?: number; Convertible?: boolean};
type Summary = {Count?: number; HiddenCount?: number; Levels?: number; Cycles?: number; TotalStandardCost?: any; StandardCostCurrency?: string | null;
                CostComplete?: boolean; UncostedLines?: number; Configuration?: {USID: string; Name?: string | null} | null;
                RollUpComplete?: boolean; UnconvertedLines?: number};
type Configuration = {USID: string; Name: string; Extension?: string | null; IsDefault?: boolean; Status?: string | null};
type Unit = {Value: string; Name?: string | null; Dimension?: string | null};
type Finding = {Finding?: string; Why?: string};
/** A capture of the bill against a revision: at release, or by hand with a reason (2026-10-31). */
type Snapshot = {USID: string; ItemRevisionUSID: string; ItemDisplayRevision?: string | null; Reason: string; Fingerprint: string;
                 CreatedDatetime?: string | null; TopLevelLines?: number};
type SnapshotNode = {itemObjectUSID: string; objectID?: string | null; name?: string | null; displayRevision?: string | null;
                     quantity?: string | null; unitOfMeasure?: string | null; referenceDesignator?: string | null;
                     pinnedRevisionUSID?: string | null; substituteForUSID?: string | null; truncated?: boolean; children?: SnapshotNode[]};

/** The captured tree flattened into the explosion's rows, quantities extended down the path. */
function flatten(nodes: SnapshotNode[], level: number, path: string[], multiplier: number, out: Line[]): Line[] {
    for (const node of nodes) {
        const quantity = node.quantity != null ? Number(node.quantity) : null;
        const extended = quantity != null && !node.substituteForUSID ? multiplier * quantity : null;
        out.push({Level: level, Path: [...path, node.objectID ?? ""], ObjectUSID: node.itemObjectUSID, ObjectID: node.objectID, Name: node.name,
                  Revision: node.displayRevision, Visible: true, Pinned: Boolean(node.pinnedRevisionUSID), Quantity: node.quantity,
                  UnitOfMeasure: node.unitOfMeasure, ExtendedQuantity: extended, ReferenceDesignator: node.referenceDesignator,
                  Substitute: Boolean(node.substituteForUSID), HasChildren: (node.children ?? []).length > 0});
        if (node.children?.length) flatten(node.children, level + 1, [...path, node.objectID ?? ""], extended ?? multiplier, out);
    }
    return out;
}


function listOf<T>(held: T[] | T | undefined | null): T[] {
    if (held === null || held === undefined) return [];
    return Array.isArray(held) ? held : [held];
}

function qbe(idoc: string, component: string, members: {Name: string; Value: any; Operator: string}[], limit: number) {
    return btoa(JSON.stringify({PlatformMessage: {"-idoc": idoc, [component]: {
        _attributes: {limit, sort: null, offset: 0},
        QueryGroups: {QueryGroup: [{Name: "primary", Operator: "AND", Type: "QueryGroup",
                                    Membership: {Member: members.map(m => ({Type: "Attribute", ...m}))}}]}}}}));
}

/** The positions a designator names, ranges expanded, mirroring the service's reading
 *  (reference_designators.py) so the dialog can say "4 positions" under B1-B4 before the save.
 *  Null when the text is not a range this reads; the service's refusal says why. */
function positionsOf(text: string): number | null {
    let count = 0;
    for (const token of text.trim().split(/[,;\s]+/)) {
        if (!token) continue;
        const range = token.includes("..") ? token.split("..") : token.split("-");
        if (range.length === 2) {
            const a = /^([A-Za-z_]*)(\d+)$/.exec(range[0]), b = /^([A-Za-z_]*)(\d+)$/.exec(range[1]);
            if (!a || !b || a[1] !== b[1] || Number(a[2]) > Number(b[2])) return null;
            count += Number(b[2]) - Number(a[2]) + 1;
        } else if (range.length === 1) {
            count += 1;
        } else {
            return null;
        }
    }
    return count;
}

/** The change-control findings a write came back with, or none. */
function findingsIn(response: any): Finding[] {
    return listOf<Finding>(response?.data?.PlatformMessage?.ListOfBomChangeFinding?.BomChangeFinding);
}

export default function ItemBillOfMaterialsPane({objectUSID, status, canEdit}: {
    /** The item's LOGICAL object. Edges hang off the object, never a revision. */
    objectUSID: string;
    status?: string | null;
    /** Whether the reader holds the item's edit on this record; the editor is hidden without it
     *  (the server refuses regardless). */
    canEdit?: boolean;
}) {
    const appContext: any = useContext(AppContext);
    const api = makeCresAxios(appContext?.sessionContext);
    const navigate = useNavigate();
    // "compare" reads two states of the bill side by side (2026-10-31); it loads no explosion.
    const [direction, setDirection] = useState<"down" | "up" | "rolled" | "compare">("down");
    const [importing, setImporting] = useState(false);
    const [rolled, setRolled] = useState<Rolled[]>([]);
    const [configurations, setConfigurations] = useState<Configuration[]>([]);
    const [configuration, setConfiguration] = useState<string>("");
    const [lines, setLines] = useState<Line[] | null>(null);
    const [summary, setSummary] = useState<Summary>({});
    const [problem, setProblem] = useState<string | null>(null);
    // The editor's own state.
    const [typeUSID, setTypeUSID] = useState<string | null>(null);
    const [units, setUnits] = useState<Unit[]>([]);
    // {} adds a line; {line} edits one; {substituteFor} adds a substitute for that line (2026-10-31).
    const [editing, setEditing] = useState<{line?: Line; substituteFor?: Line} | null>(null);
    // The configuration dialog: {} to create one, {existing} to rename or re-default one.
    const [configurationDialog, setConfigurationDialog] = useState<{existing?: Configuration} | null>(null);
    const [retiring, setRetiring] = useState<Configuration | null>(null);
    const [retireBusy, setRetireBusy] = useState(false);
    const [confirming, setConfirming] = useState<string | null>(null);
    const [removing, setRemoving] = useState<string | null>(null);
    const [findings, setFindings] = useState<Finding[]>([]);
    // WHAT A RELEASED REVISION CONTAINED (2026-10-31): the captures, and the one being read.
    const [snapshots, setSnapshots] = useState<Snapshot[]>([]);
    const [snapshot, setSnapshot] = useState<string>("");
    const [captured, setCaptured] = useState<{tree: Line[]; head: Snapshot; matches: boolean} | null>(null);

    const frozen = status != null && FROZEN.includes(String(status).toUpperCase());
    const editable = canEdit === true && !frozen;

    const loadConfigurations = useCallback(() => {
        api.get("t1/v1/bom_configurations", {params: {ItemObjectUSID: objectUSID}})
            .then((response: any) => setConfigurations(listOf<Configuration>(response?.data?.PlatformMessage?.ListOfBOMConfiguration?.BOMConfiguration)))
            .catch(() => setConfigurations([]));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [objectUSID]);
    useEffect(() => { loadConfigurations(); }, [loadConfigurations]);
    const loadSnapshots = useCallback(() => {
        api.get("t1/v1/bom_snapshots", {params: {ItemObjectUSID: objectUSID}})
            .then((response: any) => setSnapshots(listOf<Snapshot>(response?.data?.PlatformMessage?.ListOfBillOfMaterialsSnapshot?.BillOfMaterialsSnapshot)))
            .catch(() => setSnapshots([]));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [objectUSID]);
    useEffect(() => { loadSnapshots(); }, [loadSnapshots]);
    useEffect(() => {
        if (!snapshot) { setCaptured(null); return; }
        api.get("t1/v1/bom_snapshots", {params: {USID: snapshot}})
            .then((response: any) => {
                const got = response?.data?.PlatformMessage?.BillOfMaterialsSnapshot;
                if (!got) { setCaptured(null); return; }
                setCaptured({tree: flatten(listOf<SnapshotNode>(got.BillOfMaterials), 1, [got.ItemObjectID ?? ""], 1, []), head: got, matches: got.FingerprintMatches !== false});
            })
            .catch((failure: any) => { setCaptured(null); setProblem(failure?.response?.data?.Cause ?? "The snapshot could not be read."); });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [snapshot]);

    // THE TYPE AND THE UNITS ARE READ ONLY FOR AN EDITOR: a reader who cannot edit never asks for them.
    useEffect(() => {
        if (!editable) return;
        let cancelled = false;
        api.get("t1/v1/object_relationship_types", {
            params: {QBE: qbe("v1.ObjectRelationshipType-001", "ObjectRelationshipType",
                               [{Name: "Name", Value: EDGE_TYPE, Operator: "EQUAL"}], 1)},
        }).then((response: any) => {
            if (cancelled) return;
            const found = listOf<any>(response?.data?.PlatformMessage?.ListOfObjectRelationshipType?.ObjectRelationshipType);
            setTypeUSID(found[0]?.USID ?? null);
        }).catch(() => { if (!cancelled) setTypeUSID(null); });
        api.get("t1/v1/units_of_measure")
            .then((response: any) => { if (!cancelled) setUnits(listOf<Unit>(response?.data?.PlatformMessage?.ListOfUnitOfMeasure?.UnitOfMeasure)); })
            .catch(() => { if (!cancelled) setUnits([]); });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editable]);

    const load = useCallback(() => {
        if (!objectUSID) return;
        if (direction === "compare") { setLines([]); setRolled([]); return; }
        setLines(null);
        const url = direction === "up" ? "t1/v1/bom_where_used" : "t1/v1/bom_explosion";
        const params: any = {ItemObjectUSID: objectUSID};
        if (direction !== "up" && configuration) params.ConfigurationUSID = configuration;
        api.get(url, {params})
            .then((response: any) => {
                const message = response?.data?.PlatformMessage ?? {};
                setLines(listOf<Line>(direction !== "up" ? message?.ListOfBillOfMaterialsLine?.BillOfMaterialsLine
                                                        : message?.ListOfWhereUsedLine?.WhereUsedLine));
                setRolled(listOf<Rolled>(message?.ListOfRolledUpComponent?.RolledUpComponent));
                setSummary(message);
                setProblem(null);
            })
            .catch((failure: any) => { setLines([]); setProblem(failure?.response?.data?.Cause ?? "The bill of materials could not be read."); });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [objectUSID, direction, configuration]);
    useEffect(() => { load(); }, [load]);

    const currencies = useMemo(() => {
        try {
            const picks: any[] = appContext?.sessionContext?.getPickValues?.() ?? [];
            return picks.filter(one => one?.Type === "Currency").map(one => String(one.Value)).sort();
        } catch { return []; }
    }, [appContext]);

    const remove = (line: Line) => {
        if (!line.EdgeUSID) return;
        setRemoving(line.EdgeUSID);
        setConfirming(null);
        api.delete("t1/v1/object_relationships", {
            headers: {"idoc-request": RELATIONSHIP_IDOC},
            data: {PlatformMessage: {"-idoc": RELATIONSHIP_IDOC, ObjectRelationship: {USID: line.EdgeUSID}}},
        }).then((response: any) => { setFindings(findingsIn(response)); load(); })
          .catch((failure: any) => setProblem(failure?.response?.data?.Cause ?? "The line could not be removed."))
          .finally(() => setRemoving(null));
    };

    // RETIRING KEEPS THE ROW -- the lines that name it still read -- and only stops it being
    // offered, which is why it is a status and not a delete. The select falls back to the default.
    const retire = (one: Configuration) => {
        setRetireBusy(true);
        api.put("t1/v1/bom_configurations", {USID: one.USID, Status: "RETIRED"})
            .then(() => { setRetiring(null); setConfiguration(""); loadConfigurations(); })
            .catch((failure: any) => setProblem(failure?.response?.data?.Cause ?? "The configuration could not be retired."))
            .finally(() => setRetireBusy(false));
    };
    const selected = configurations.find(one => one.USID === configuration) ?? null;

    const own = (line: Line) => line.Level === 1 && (!line.ParentObjectUSID || line.ParentObjectUSID === objectUSID);
    const showEditor = editable && direction === "down";
    const columns = direction === "down"
        ? ["Item", "Rev", "Per parent", "Extended", "Designator", "Std cost", "Extended cost", ...(showEditor ? [""] : [])]
        : direction === "rolled"
            ? ["Component", "", "Needed for one", "Dimension", "Lines", "", ""]
            : ["Used in", "Rev", "Per parent", "Designator", "Configuration", "", ""];
    const gridColumns = showEditor ? "3fr 0.6fr 1fr 1fr 1fr 1fr 1fr auto" : "3fr 0.6fr 1fr 1fr 1fr 1fr 1fr";
    const hiddenSpan = showEditor ? 7 : 6;
    const money = (value: any, currency?: string | null) => value == null ? "—" : `${value}${currency ? ` ${currency}` : ""}`;

    return (
        <Box sx={{border: `1px solid ${Grey2}`, borderRadius: "6px", padding: "12px 14px"}}>
            <Box sx={{display: "flex", alignItems: "center", gap: "12px", marginBottom: "8px", flexWrap: "wrap"}}>
                <Typography sx={{fontSize: 14, fontWeight: 600, color: Grey8}}>Bill of materials</Typography>
                <Button size="small" variant={direction === "down" ? "contained" : "outlined"} disableElevation
                        sx={{textTransform: "none"}} onClick={() => setDirection("down")}>Explosion</Button>
                <Button size="small" variant={direction === "rolled" ? "contained" : "outlined"} disableElevation
                        sx={{textTransform: "none"}} onClick={() => setDirection("rolled")}>Rolled up</Button>
                <Button size="small" variant={direction === "up" ? "contained" : "outlined"} disableElevation
                        sx={{textTransform: "none"}} onClick={() => setDirection("up")}>Where used</Button>
                {snapshots.length > 0 ? (
                    <Button size="small" variant={direction === "compare" ? "contained" : "outlined"} disableElevation
                            sx={{textTransform: "none"}} onClick={() => setDirection("compare")}>Compare</Button>
                ) : null}
                {direction !== "up" && direction !== "compare" && (configurations.length > 0 || showEditor) ? (
                    <TextField select size="small" value={configuration} sx={{minWidth: 200}} label="Configuration"
                               onChange={e => setConfiguration(e.target.value)}
                               slotProps={{select: {displayEmpty: true}, inputLabel: {shrink: true}}}>
                        <MenuItem value="">Default</MenuItem>
                        {configurations.map(one => <MenuItem key={one.USID} value={one.USID}>{one.Name}{one.IsDefault ? " (default)" : ""}</MenuItem>)}
                    </TextField>
                ) : null}
                {direction === "down" && snapshots.length > 0 ? (
                    <TextField select size="small" value={snapshot} sx={{minWidth: 220}} label="As captured"
                               onChange={e => setSnapshot(e.target.value)}
                               slotProps={{select: {displayEmpty: true}, inputLabel: {shrink: true}}}>
                        <MenuItem value="">Now</MenuItem>
                        {snapshots.map(one => (
                            <MenuItem key={one.USID} value={one.USID}>
                                {one.ItemDisplayRevision ? `Rev ${one.ItemDisplayRevision} · ` : ""}{one.Reason}{one.CreatedDatetime ? ` · ${String(one.CreatedDatetime).slice(0, 10)}` : ""}
                            </MenuItem>
                        ))}
                    </TextField>
                ) : null}
                <Box sx={{flex: 1}}/>
                {showEditor && selected ? (retiring?.USID === selected.USID ? (
                    <Box sx={{display: "flex", alignItems: "center", gap: "8px"}}>
                        <Typography sx={{fontSize: 12.5, color: Grey8}}>Retire {selected.Name}? Its lines stay; it is no longer offered.</Typography>
                        <Typography sx={{fontSize: 12.5, color: "#B3261E", cursor: "pointer"}} onClick={() => !retireBusy && retire(selected)}>
                            {retireBusy ? "Retiring…" : "Yes"}
                        </Typography>
                        <Typography sx={{fontSize: 12.5, color: Grey7, cursor: "pointer"}} onClick={() => setRetiring(null)}>No</Typography>
                    </Box>
                ) : (
                    <>
                        <Button size="small" variant="text" sx={{textTransform: "none"}} onClick={() => setConfigurationDialog({existing: selected})}>
                            Rename…
                        </Button>
                        <Button size="small" variant="text" sx={{textTransform: "none"}} onClick={() => setRetiring(selected)}>
                            Retire
                        </Button>
                    </>
                )) : null}
                {direction === "down" && !captured && lines && lines.length > 0 ? (
                    <Button size="small" variant="text" sx={{textTransform: "none"}} onClick={() => exportCsv(lines, objectUSID)}>
                        Export CSV
                    </Button>
                ) : null}
                {showEditor ? (
                    <>
                        <Button size="small" variant="text" sx={{textTransform: "none"}} onClick={() => setConfigurationDialog({})}>
                            New configuration
                        </Button>
                        <Button size="small" variant="text" sx={{textTransform: "none"}} onClick={() => setImporting(true)}>
                            Import…
                        </Button>
                        <Button size="small" variant="outlined" sx={{textTransform: "none"}} disabled={!typeUSID}
                                onClick={() => setEditing({})}>
                            Add line
                        </Button>
                    </>
                ) : null}
            </Box>
            {canEdit === true && frozen ? (
                <Typography sx={{fontSize: 12.5, color: Grey7, marginBottom: "8px"}}>
                    A {String(status).toLowerCase()} item's bill of materials is frozen; nothing here can be changed.
                </Typography>
            ) : null}
            {findings.length > 0 ? (
                <Box sx={{border: "1px solid #F5C77E", background: "#FFF8EC", borderRadius: "6px", padding: "8px 12px", marginBottom: "10px"}}>
                    {findings.map((one, index) => (
                        <Typography key={`${one.Finding}${index}`} sx={{fontSize: 12.5, color: "#7A4B00", lineHeight: 1.5}}>
                            Saved, uncontrolled: {one.Why ?? one.Finding}
                        </Typography>
                    ))}
                    <Typography sx={{fontSize: 12, color: Blue3, cursor: "pointer", marginTop: "4px"}} onClick={() => setFindings([])}>Dismiss</Typography>
                </Box>
            ) : null}
            {lines && lines.length > 0 ? (
                <Typography sx={{fontSize: 12.5, color: Grey7, marginBottom: "10px"}}>
                    {summary.Count} line{summary.Count === 1 ? "" : "s"} over {summary.Levels} level{summary.Levels === 1 ? "" : "s"}.
                    {direction === "rolled" ? (summary.RollUpComplete
                        ? ` Every line converted: ${rolled.length} component${rolled.length === 1 ? "" : "s"} in base units.`
                        : ` ${rolled.length} component${rolled.length === 1 ? "" : "s"}; ${summary.UnconvertedLines ?? 0} line${(summary.UnconvertedLines ?? 0) === 1 ? "" : "s"} in a unit that does not convert${(summary.HiddenCount ?? 0) > 0 ? " or hidden" : ""}.`) : ""}
                    {direction === "down" ? (summary.CostComplete
                        ? ` Standard cost of one: ${summary.TotalStandardCost} ${summary.StandardCostCurrency}.`
                        : ` No standard cost total: ${summary.UncostedLines ?? 0} line${(summary.UncostedLines ?? 0) === 1 ? "" : "s"} uncosted${(summary.HiddenCount ?? 0) > 0 ? " or hidden" : ""}.`) : ""}
                    {(summary.HiddenCount ?? 0) > 0 ? ` ${summary.HiddenCount} not visible to you and not walked.` : ""}
                    {(summary.Cycles ?? 0) > 0 ? ` ${summary.Cycles} cycle${summary.Cycles === 1 ? "" : "s"} met and not followed.` : ""}
                    {showEditor ? " Lines of this item are edited here; a deeper line is edited on its own item." : ""}
                </Typography>
            ) : null}
            {problem && <Typography sx={{fontSize: 13, color: "#B3261E", marginBottom: "8px"}}>{problem}</Typography>}
            {lines === null && <Typography sx={{fontSize: 13, color: Grey7}}>Reading…</Typography>}
            {lines && lines.length === 0 && direction !== "compare" && (
                <Typography sx={{fontSize: 13, color: Grey7, lineHeight: 1.6}}>
                    {direction !== "up"
                        ? (showEditor ? "This item's bill of materials has no lines yet. Add one with the button above."
                                      : "This item's bill of materials has no lines.")
                        : "No bill of materials names this item."}
                </Typography>
            )}
            {lines && lines.length > 0 && direction === "rolled" && (
                <Box sx={{display: "grid", gridTemplateColumns: "3fr 0.6fr 1fr 1fr 1fr 1fr 1fr", gap: "4px 12px", alignItems: "center"}}>
                    {columns.map((head, index) => (
                        <Typography key={`${head}${index}`} sx={{fontSize: 11.5, color: Grey7, textTransform: "uppercase", letterSpacing: "0.04em"}}>{head}</Typography>
                    ))}
                    {rolled.map(row => {
                        const line = lines.find(one => one.ObjectUSID === row.ObjectUSID && one.Visible);
                        return (
                            <React.Fragment key={row.ObjectUSID}>
                                <Typography sx={{fontSize: 13, color: Blue3, cursor: "pointer"}}
                                            onClick={() => line?.RevisionUSID && navigate(`/object/${line.RevisionUSID}`)}>
                                    {row.ObjectID ?? ""} {row.Name ?? ""}
                                </Typography>
                                <span/>
                                <Typography sx={{fontSize: 12.5, color: row.Convertible ? Grey17 : Grey6}} title={row.Convertible ? "In the dimension's base unit, every path added up" : "A line of this component is in a unit with no factor to the base, so the paths cannot be added."}>
                                    {row.Convertible ? `${row.Quantity ?? "—"}${row.UnitOfMeasure ? ` ${row.UnitOfMeasure}` : ""}` : "does not convert"}
                                </Typography>
                                <Typography sx={{fontSize: 12.5, color: Grey8}}>{row.Dimension ?? "—"}</Typography>
                                <Typography sx={{fontSize: 12.5, color: Grey8}}>{row.Lines ?? "—"}</Typography>
                                <span/><span/>
                            </React.Fragment>
                        );
                    })}
                </Box>
            )}
            {direction === "compare" ? <ComparePanel api={api} objectUSID={objectUSID} snapshots={snapshots}/> : null}
            {captured && direction === "down" ? (
                <Typography sx={{fontSize: 12.5, color: "#7A4B00", background: "#FFF8EC", border: "1px solid #F5C77E", borderRadius: "6px", padding: "6px 10px", marginBottom: "10px"}}>
                    As captured{captured.head.ItemDisplayRevision ? ` against revision ${captured.head.ItemDisplayRevision}` : ""}: {captured.head.Reason}
                    {captured.head.CreatedDatetime ? ` on ${String(captured.head.CreatedDatetime).slice(0, 10)}` : ""}. {captured.tree.length} line{captured.tree.length === 1 ? "" : "s"}; read-only.
                    {captured.matches ? " The fingerprint matches what was captured." : " THE FINGERPRINT DOES NOT MATCH: something reached past the application."}
                </Typography>
            ) : null}
            {((captured && direction === "down") || (lines && lines.length > 0 && direction !== "rolled")) && (
                <Box sx={{display: "grid", gridTemplateColumns: direction === "down" ? (captured ? "3fr 0.6fr 1fr 1fr 1fr 1fr 1fr" : gridColumns) : "3fr 0.6fr 1fr 1fr 1fr 1fr 1fr", gap: "4px 12px", alignItems: "center"}}>
                    {(captured && direction === "down" ? columns.slice(0, 7) : columns).map((head, index) => (
                        <Typography key={`${head}${index}`} sx={{fontSize: 11.5, color: Grey7, textTransform: "uppercase", letterSpacing: "0.04em"}}>{head}</Typography>
                    ))}
                    {(captured && direction === "down" ? captured.tree : (lines ?? [])).map(line => {
                        const indent = {paddingLeft: `${(line.Level - 1) * 18}px`};
                        const key = (line.EdgeUSID ?? line.ObjectUSID) + line.Path.join(">");
                        if (!line.Visible) {
                            return (
                                <React.Fragment key={key}>
                                    <Typography sx={{fontSize: 13, color: Grey6, ...indent}} title="This item is not visible to you.">{line.ObjectID ?? "—"}</Typography>
                                    <Typography sx={{fontSize: 13, color: Grey6, fontStyle: "italic", gridColumn: `span ${direction === "down" ? hiddenSpan : 6}`}}>Not visible to you</Typography>
                                </React.Fragment>
                            );
                        }
                        return (
                            <React.Fragment key={key}>
                                <Typography sx={{fontSize: 13, color: Blue3, cursor: "pointer", ...indent, ...(line.Substitute ? {paddingLeft: `${(line.Level - 1) * 18 + 18}px`, fontStyle: "italic"} : {})}}
                                            onClick={() => line.RevisionUSID && navigate(`/object/${line.RevisionUSID}`)}
                                            title={line.Substitute ? "A substitute: may stand in for the line above; counted in nothing until chosen." : undefined}>
                                    {line.Substitute ? "or " : ""}{line.ObjectID ?? ""} {line.Name ?? ""}{line.Cycle ? "  (cycle)" : ""}
                                </Typography>
                                <Typography sx={{fontSize: 12.5, color: Grey8}} title={line.Pinned ? "Pinned to this revision" : "Follows the current revision"}>
                                    {line.Revision ?? "—"}{line.Pinned ? " ⚲" : ""}
                                </Typography>
                                <Typography sx={{fontSize: 12.5, color: Grey8}}>{line.Quantity ?? "—"}{line.UnitOfMeasure ? ` ${line.UnitOfMeasure}` : ""}</Typography>
                                {direction === "down" ? (
                                    <>
                                        <Typography sx={{fontSize: 12.5, color: Grey17}}
                                                    title={line.BaseQuantity != null && line.BaseUnit && line.BaseUnit !== line.UnitOfMeasure ? `${line.BaseQuantity} ${line.BaseUnit} in the ${line.Dimension ?? ""} base unit` : undefined}>
                                            {line.ExtendedQuantity ?? "—"}{line.UnitOfMeasure ? ` ${line.UnitOfMeasure}` : ""}
                                        </Typography>
                                        <Typography sx={{fontSize: 12.5, color: Grey8}}>{line.ReferenceDesignator ?? "—"}</Typography>
                                        <Typography sx={{fontSize: 12.5, color: Grey8}}>{money(line.StandardCost, line.StandardCostCurrency)}</Typography>
                                        <Typography sx={{fontSize: 12.5, color: Grey8}}>{money(line.ExtendedStandardCost, line.StandardCostCurrency)}</Typography>
                                        {showEditor && !captured ? (own(line) ? (
                                            <Box sx={{display: "flex", gap: "10px", whiteSpace: "nowrap"}}>
                                                {confirming === line.EdgeUSID ? (
                                                    <>
                                                        <Typography sx={{fontSize: 12.5, color: Grey8}}>Remove this line?</Typography>
                                                        <Typography sx={{fontSize: 12.5, color: "#B3261E", cursor: "pointer"}} onClick={() => remove(line)}>Yes</Typography>
                                                        <Typography sx={{fontSize: 12.5, color: Grey7, cursor: "pointer"}} onClick={() => setConfirming(null)}>No</Typography>
                                                    </>
                                                ) : (
                                                    <>
                                                        <Typography sx={{fontSize: 12.5, color: Blue3, cursor: "pointer"}} onClick={() => setEditing({line})}>Edit</Typography>
                                                        {!line.Substitute ? (
                                                            <Typography sx={{fontSize: 12.5, color: Blue3, cursor: "pointer"}} onClick={() => setEditing({substituteFor: line})}
                                                                        title="Another component that may stand in for this one on this line.">Add substitute</Typography>
                                                        ) : null}
                                                        <Typography sx={{fontSize: 12.5, color: Grey7, cursor: "pointer"}}
                                                                    onClick={() => removing !== line.EdgeUSID && setConfirming(line.EdgeUSID ?? null)}>
                                                            {removing === line.EdgeUSID ? "Removing…" : "Remove"}
                                                        </Typography>
                                                    </>
                                                )}
                                            </Box>
                                        ) : (
                                            <Typography sx={{fontSize: 12, color: Grey6, whiteSpace: "nowrap"}}
                                                        title={`A line of ${line.Path[line.Path.length - 2] ?? "its parent"}'s bill; edit it on that item.`}>
                                                on {line.Path[line.Path.length - 2] ?? "its parent"}
                                            </Typography>
                                        )) : null}
                                    </>
                                ) : (
                                    <>
                                        <Typography sx={{fontSize: 12.5, color: Grey8}}>{line.ReferenceDesignator ?? "—"}</Typography>
                                        <Typography sx={{fontSize: 12.5, color: Grey8}}>{line.Configuration ?? "—"}</Typography>
                                        <span/><span/>
                                    </>
                                )}
                            </React.Fragment>
                        );
                    })}
                </Box>
            )}
            {editing && typeUSID && (
                <LineDialog api={api} parentUSID={objectUSID} typeUSID={typeUSID} units={units} currencies={currencies}
                            configurations={configurations.filter(one => !one.Status || String(one.Status).toUpperCase() === "ACTIVE")}
                            initialConfiguration={editing.line ? (editing.line.ConfigurationUSID ?? "") : configuration}
                            line={editing.line} substituteFor={editing.substituteFor} onClose={() => setEditing(null)}
                            onSaved={found => { setEditing(null); setFindings(found); load(); loadSnapshots(); }}/>
            )}
            {importing && (
                <ImportDialog api={api} parentUSID={objectUSID} onClose={() => setImporting(false)}
                              onImported={() => { load(); loadSnapshots(); }}/>
            )}
            {configurationDialog && (
                <ConfigurationDialog api={api} parentUSID={objectUSID} existing={configurationDialog.existing}
                                     onClose={() => setConfigurationDialog(null)}
                                     onSaved={usid => { setConfigurationDialog(null); loadConfigurations(); setConfiguration(usid); }}/>
            )}
        </Box>
    );
}

/** One line, added or edited: the component, how many in what unit, where it sits, what it costs,
 *  which configuration, and whether it follows the component's current revision or is pinned. */
function LineDialog({api, parentUSID, typeUSID, units, currencies, configurations, initialConfiguration, line, substituteFor, onClose, onSaved}: {
    api: any; parentUSID: string; typeUSID: string; units: Unit[]; currencies: string[]; configurations: Configuration[];
    initialConfiguration: string; line?: Line;
    /** The line a NEW substitute stands in for. A substitute sits where its primary sits and in its
     *  configuration, so neither is asked; the service refuses either if sent. */
    substituteFor?: Line;
    onClose: () => void; onSaved: (findings: Finding[]) => void;
}) {
    // A SUBSTITUTE, new or being edited, names the line it stands in for.
    const standsInFor = substituteFor?.EdgeUSID ?? (line?.Substitute ? line.SubstituteForEdgeUSID ?? undefined : undefined);
    const [component, setComponent] = useState<PickedRecord | null>(
        line ? {ObjectUSID: line.ObjectUSID, RevisionUSID: line.RevisionUSID ?? "", ObjectID: line.ObjectID ?? null, Name: line.Name ?? null, Status: line.Status ?? null} : null);
    const [choosing, setChoosing] = useState(false);
    const [quantity, setQuantity] = useState(line?.Quantity != null ? String(line.Quantity) : "");
    const [unit, setUnit] = useState(line?.UnitOfMeasure ?? "");
    const [designator, setDesignator] = useState(line?.ReferenceDesignator ?? "");
    const [cost, setCost] = useState(line?.StandardCost != null ? String(line.StandardCost) : "");
    const [currency, setCurrency] = useState(line?.StandardCostCurrency ?? "");
    const [configuration, setConfiguration] = useState(initialConfiguration);
    const [pinned, setPinned] = useState(Boolean(line?.Pinned));
    const [busy, setBusy] = useState(false);
    const [problem, setProblem] = useState<string | null>(null);

    // A UNIT THE LIST DOES NOT HOLD (an edge older than the list) is still offered, so an edit of
    // the quantity alone does not silently change the unit.
    const unitChoices = useMemo(() => {
        const known = units.map(one => one.Value);
        return unit && !known.includes(unit) ? [{Value: unit, Name: unit, Dimension: null}, ...units] : units;
    }, [units, unit]);
    const currencyChoices = useMemo(() => currency && !currencies.includes(currency) ? [currency, ...currencies] : currencies, [currencies, currency]);

    const save = () => {
        if (!component?.ObjectUSID) { setProblem("Choose the component first."); return; }
        if (!quantity.trim()) { setProblem("How many of the component go into one of this item?"); return; }
        if (!unit) { setProblem("Choose the unit the quantity is counted in."); return; }
        if ((cost.trim() && !currency) || (!cost.trim() && currency)) { setProblem("A standard cost needs both an amount and a currency."); return; }
        setBusy(true);
        setProblem(null);
        // THE WHOLE LINE, every time: a field left blank is sent as null and cleared, so the person
        // sees exactly what is stored. On a new line, pinning takes the revision the chooser showed,
        // which is the component's current one; on an edit of a floating line, the same.
        const record: any = {
            ParentUSID: parentUSID, ChildUSID: component.ObjectUSID, ObjectRelationshipTypeUSID: typeUSID,
            Quantity: quantity.trim(), UnitOfMeasure: unit,
            ReferenceDesignator: standsInFor ? null : (designator.trim() || null),
            StandardCost: cost.trim() || null, StandardCostCurrency: cost.trim() ? currency : null,
            BOMConfigurationUSID: standsInFor ? null : (configuration || null),
            ChildPinnedRevisionUSID: pinned ? (component.RevisionUSID || null) : null,
            SubstituteForUSID: standsInFor ?? null,
        };
        if (line?.EdgeUSID) record.USID = line.EdgeUSID;
        const call = line?.EdgeUSID
            ? api.put("t1/v1/object_relationships", {PlatformMessage: {"-idoc": RELATIONSHIP_IDOC, ObjectRelationship: record}},
                      {headers: {"idoc-request": RELATIONSHIP_IDOC, "idoc-response": RELATIONSHIP_IDOC}})
            : api.post("t1/v1/object_relationships", {PlatformMessage: {"-idoc": RELATIONSHIP_IDOC, ObjectRelationship: record}},
                       {headers: {"idoc-request": RELATIONSHIP_IDOC, "idoc-response": RELATIONSHIP_IDOC}});
        call.then((response: any) => onSaved(findingsIn(response)))
            .catch((failure: any) => { setProblem(failure?.response?.data?.Cause ?? "The line could not be saved."); setBusy(false); });
    };

    const field = {size: "small" as const, fullWidth: true};
    return (
        <Dialog open onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle sx={{fontSize: 16, paddingBottom: "6px"}}>
                {substituteFor ? `Add a substitute for ${substituteFor.ObjectID ?? ""} ${substituteFor.Name ?? ""}`.trim() : line ? (line.Substitute ? "Edit substitute" : "Edit line") : "Add line"}
            </DialogTitle>
            <DialogContent sx={{display: "flex", flexDirection: "column", gap: "14px", paddingTop: "8px !important"}}>
                <Box sx={{display: "flex", alignItems: "center", gap: "12px"}}>
                    <Typography sx={{fontSize: 13, color: Grey7, minWidth: 90}}>Component</Typography>
                    <Typography sx={{fontSize: 13, color: component ? Grey17 : Grey6, flex: 1}}>
                        {component ? `${component.ObjectID ?? ""} ${component.Name ?? ""}`.trim() : "none chosen"}
                    </Typography>
                    {!line ? (
                        <Button size="small" variant="outlined" sx={{textTransform: "none"}} onClick={() => setChoosing(true)}>
                            {component ? "Change…" : "Choose item…"}
                        </Button>
                    ) : null}
                </Box>
                <Box sx={{display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px"}}>
                    <TextField {...field} label="Quantity per one" value={quantity} onChange={e => setQuantity(e.target.value)}
                               slotProps={{htmlInput: {inputMode: "decimal", "aria-label": "Quantity per one"}}}/>
                    <TextField {...field} select label="Unit" value={unit} onChange={e => setUnit(e.target.value)}
                               slotProps={{htmlInput: {"aria-label": "Unit"}}}>
                        {unitChoices.map(one => (
                            <MenuItem key={one.Value} value={one.Value}>{one.Value}{one.Name && one.Name !== one.Value ? ` — ${one.Name}` : ""}{one.Dimension ? ` (${one.Dimension})` : ""}</MenuItem>
                        ))}
                    </TextField>
                    {standsInFor ? (
                        <Typography sx={{fontSize: 12.5, color: Grey7, gridColumn: "span 2"}}>
                            A substitute sits where its primary sits and in its configuration, so neither is asked. It is counted in nothing until somebody chooses it.
                        </Typography>
                    ) : null}
                    {standsInFor ? null : <TextField {...field} label="Reference designator" value={designator} onChange={e => setDesignator(e.target.value)}
                               slotProps={{htmlInput: {"aria-label": "Reference designator"}}} placeholder="Where it sits: A1, R12, B1-B4"
                               helperText={designator.trim()
                                   ? (positionsOf(designator) === null ? "Not a range this reads: B1-B4, same prefix, first not above second."
                                      : `${positionsOf(designator)} position${positionsOf(designator) === 1 ? "" : "s"}${(positionsOf(designator) ?? 0) > 1 && quantity.trim() && Number(quantity) !== positionsOf(designator) ? `; the quantity is ${quantity.trim()}` : ""}`)
                                   : "A list or a range; several positions must match the quantity"}/>}
                    {standsInFor ? null : <TextField {...field} select label="Configuration" value={configuration} onChange={e => setConfiguration(e.target.value)}
                               slotProps={{htmlInput: {"aria-label": "Configuration"}}}>
                        <MenuItem value="">Base (every configuration)</MenuItem>
                        {configurations.map(one => <MenuItem key={one.USID} value={one.USID}>{one.Name}{one.IsDefault ? " (default)" : ""}</MenuItem>)}
                    </TextField>}
                    <TextField {...field} label="Standard cost of one" value={cost} onChange={e => setCost(e.target.value)}
                               slotProps={{htmlInput: {inputMode: "decimal", "aria-label": "Standard cost of one"}}}/>
                    <TextField {...field} select label="Currency" value={currency} onChange={e => setCurrency(e.target.value)}
                               slotProps={{htmlInput: {"aria-label": "Currency"}}}>
                        <MenuItem value="">—</MenuItem>
                        {currencyChoices.map(one => <MenuItem key={one} value={one}>{one}</MenuItem>)}
                    </TextField>
                </Box>
                <FormControlLabel control={<Checkbox size="small" checked={pinned} onChange={e => setPinned(e.target.checked)}/>}
                                  label={<Typography sx={{fontSize: 13, color: Grey8}}>
                                      Pin to the component's {line?.Pinned ? "pinned" : "current"} revision{component?.RevisionUSID ? "" : " (none known)"}; unpinned, the line follows whatever is current
                                  </Typography>}/>
                {problem && <Typography sx={{fontSize: 13, color: "#B3261E"}}>{problem}</Typography>}
            </DialogContent>
            <DialogActions sx={{padding: "12px 24px"}}>
                <Button onClick={onClose} sx={{textTransform: "none"}} disabled={busy}>Cancel</Button>
                <Button variant="contained" disableElevation onClick={save} disabled={busy} sx={{textTransform: "none"}}>
                    {busy ? "Saving…" : "Save"}
                </Button>
            </DialogActions>
            {choosing && (
                <ItemChooser onClose={() => setChoosing(false)}
                             onChoose={picked => { setComponent(picked); setChoosing(false); }}/>
            )}
        </Dialog>
    );
}

/** The chooser: the Items list screen in picker mode, one item. The service refuses the item itself
 *  (CC-ORBM-SELF) and anything that already contains it (CC-ORBM-CYCLE), and that refusal is shown
 *  as the server phrased it rather than second-guessed here. */
function ItemChooser({onClose, onChoose}: {onClose: () => void; onChoose: (picked: PickedRecord) => void}) {
    const [ticked, setTicked] = useState<PickedRecord[]>([]);
    const classification = OBJECT_CLASSIFICATIONS.find(one => one.code === "Item");
    if (!classification) return null;
    const chosen = ticked.filter(one => one.ObjectUSID);
    return (
        <Dialog open onClose={onClose} fullWidth maxWidth="xl"
                slotProps={{paper: {sx: {height: "90vh", maxHeight: "90vh", display: "flex", flexDirection: "column"}}}}>
            <DialogTitle sx={{fontSize: 16, paddingBottom: "6px", flex: "0 0 auto"}}>Choose the component</DialogTitle>
            <DialogContent sx={{padding: 0, flex: "1 1 auto", minHeight: 0, overflow: "hidden", display: "flex", flexDirection: "column"}}>
                <ObjectList classification={classification} picker={{multiple: false, onSelectionChange: setTicked}}/>
            </DialogContent>
            <DialogActions sx={{padding: "12px 24px", borderTop: "1px solid #E4E7EC", flex: "0 0 auto"}}>
                <Button onClick={onClose} sx={{textTransform: "none"}}>Cancel</Button>
                <Button variant="contained" disableElevation disabled={chosen.length !== 1} sx={{textTransform: "none"}}
                        onClick={() => onChoose(chosen[0])}>
                    Select
                </Button>
            </DialogActions>
        </Dialog>
    );
}

/** A configuration of this item's bill, new or renamed: a name, an optional extension, and whether it
 *  is the default the explosion takes when none is named. On an existing one the service keeps what is
 *  not sent, and a default unticked DEMOTES it -- the item is then left with no default, which is a
 *  state it starts in -- so the box is sent exactly as it stands. */
function ConfigurationDialog({api, parentUSID, existing, onClose, onSaved}: {
    api: any; parentUSID: string; existing?: Configuration; onClose: () => void; onSaved: (usid: string) => void;
}) {
    const [name, setName] = useState(existing?.Name ?? "");
    const [extension, setExtension] = useState(existing?.Extension ?? "");
    const [isDefault, setIsDefault] = useState(Boolean(existing?.IsDefault));
    const [busy, setBusy] = useState(false);
    const [problem, setProblem] = useState<string | null>(null);
    const save = () => {
        if (!name.trim()) { setProblem("Name the configuration."); return; }
        setBusy(true);
        const call = existing
            ? api.put("t1/v1/bom_configurations", {USID: existing.USID, Name: name.trim(), Extension: extension.trim() || null, IsDefault: isDefault})
            : api.post("t1/v1/bom_configurations", {ItemObjectUSID: parentUSID, Name: name.trim(), Extension: extension.trim() || null, IsDefault: isDefault});
        call.then((response: any) => onSaved(String(response?.data?.PlatformMessage?.USID ?? existing?.USID ?? "")))
            .catch((failure: any) => { setProblem(failure?.response?.data?.Cause ?? `The configuration could not be ${existing ? "changed" : "created"}.`); setBusy(false); });
    };
    return (
        <Dialog open onClose={onClose} fullWidth maxWidth="xs">
            <DialogTitle sx={{fontSize: 16, paddingBottom: "6px"}}>{existing ? "Rename configuration" : "New configuration"}</DialogTitle>
            <DialogContent sx={{display: "flex", flexDirection: "column", gap: "12px", paddingTop: "8px !important"}}>
                <TextField size="small" fullWidth label="Name" value={name} onChange={e => setName(e.target.value)} slotProps={{htmlInput: {"aria-label": "Configuration name"}}}/>
                <TextField size="small" fullWidth label="Extension" value={extension} onChange={e => setExtension(e.target.value)} placeholder="-S" slotProps={{htmlInput: {"aria-label": "Configuration extension"}}}/>
                <FormControlLabel control={<Checkbox size="small" checked={isDefault} onChange={e => setIsDefault(e.target.checked)}/>}
                                  label={<Typography sx={{fontSize: 13, color: Grey8}}>The default: taken when no configuration is named</Typography>}/>
                {problem && <Typography sx={{fontSize: 13, color: "#B3261E"}}>{problem}</Typography>}
            </DialogContent>
            <DialogActions sx={{padding: "12px 24px"}}>
                <Button onClick={onClose} sx={{textTransform: "none"}} disabled={busy}>Cancel</Button>
                <Button variant="contained" disableElevation onClick={save} disabled={busy} sx={{textTransform: "none"}}>
                    {busy ? "Saving…" : (existing ? "Save" : "Create")}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

/** How a capture is named wherever it is offered: its revision, its reason, its date. */
function snapshotLabel(one: Snapshot): string {
    return `${one.ItemDisplayRevision ? `Rev ${one.ItemDisplayRevision} · ` : ""}${one.Reason}${one.CreatedDatetime ? ` · ${String(one.CreatedDatetime).slice(0, 10)}` : ""}`;
}

// ── export: the explosion as a CSV, built here (2026-10-31) ──────────────────────────────────────
// Nothing is downloaded through a function. The columns are named as the import reads them, so a
// bill exported from one item imports into another: Level 1 rows are the item's own lines.
const EXPORT_COLUMNS = ["Level", "ComponentObjectID", "Name", "Revision", "Quantity", "UnitOfMeasure", "ExtendedQuantity",
                        "ReferenceDesignator", "StandardCost", "StandardCostCurrency", "Configuration", "Pinned", "Substitute"];

function csvCell(value: any): string {
    if (value == null) return "";
    const text = String(value);
    return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, "\"\"")}"` : text;
}

export function explosionCsv(lines: Line[]): string {
    const rows = lines.filter(line => line.Visible !== false).map(line => [
        line.Level, line.ObjectID, line.Name, line.Revision, line.Quantity, line.UnitOfMeasure, line.ExtendedQuantity,
        line.ReferenceDesignator, line.StandardCost, line.StandardCostCurrency, line.Configuration,
        line.Pinned ? "yes" : "", line.Substitute ? "yes" : "",
    ].map(csvCell).join(","));
    return [EXPORT_COLUMNS.join(","), ...rows].join("\r\n") + "\r\n";
}

function exportCsv(lines: Line[], objectUSID: string) {
    const blob = new Blob([explosionCsv(lines)], {type: "text/csv;charset=utf-8"});
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `bill-of-materials-${objectUSID.slice(0, 8)}.csv`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// ── import: rows pasted or chosen, parsed here, sent as rows (2026-10-31) ────────────────────────
/** Comma- or tab-separated text with a header row, quotes honoured. */
export function parseDelimited(text: string): {headers: string[]; rows: string[][]} {
    const lines = text.replace(/\r\n?/g, "\n").split("\n").filter(line => line.trim() !== "");
    if (lines.length === 0) return {headers: [], rows: []};
    const delimiter = lines[0].includes("\t") ? "\t" : ",";
    const split = (line: string): string[] => {
        const out: string[] = [];
        let cell = "", quoted = false;
        for (let i = 0; i < line.length; i += 1) {
            const char = line[i];
            if (quoted) {
                if (char === "\"" && line[i + 1] === "\"") { cell += "\""; i += 1; }
                else if (char === "\"") quoted = false;
                else cell += char;
            } else if (char === "\"") quoted = true;
            else if (char === delimiter) { out.push(cell); cell = ""; }
            else cell += char;
        }
        out.push(cell);
        return out.map(one => one.trim());
    };
    const headers = split(lines[0]);
    return {headers, rows: lines.slice(1).map(split)};
}

/** The import's field for a column heading, as people write them: part number, qty, UoM, ref des, config. */
const HEADINGS: [RegExp, string][] = [
    [/^(component\s*)?(object\s*)?id$|^part(\s*(number|no\.?|#))?$|^item(\s*id)?$|^component$/i, "ComponentObjectID"],
    [/^(quantity|qty|per\s*parent)$/i, "Quantity"],
    [/^(unit(\s*of\s*measure)?|uom|unit)$/i, "UnitOfMeasure"],
    [/^(reference\s*)?designators?$|^ref(\s*des)?\.?$/i, "ReferenceDesignator"],
    [/^(standard\s*)?cost$|^std\s*cost$|^unit\s*cost$/i, "StandardCost"],
    [/^(standard\s*)?(cost\s*)?currency$/i, "StandardCostCurrency"],
    [/^config(uration)?$/i, "Configuration"],
    [/^pinned$/i, "Pinned"],
    [/^level$/i, "Level"],
];

export function importRows(text: string): {rows: Record<string, string>[]; unmapped: string[]; skipped: number; missing: string[]} {
    const {headers, rows} = parseDelimited(text);
    const fields = headers.map(heading => HEADINGS.find(([pattern]) => pattern.test(heading))?.[1] ?? null);
    const unmapped = headers.filter((_, index) => fields[index] == null);
    const missing = ["ComponentObjectID", "Quantity", "UnitOfMeasure"].filter(one => !fields.includes(one));
    let skipped = 0;
    const out: Record<string, string>[] = [];
    for (const row of rows) {
        const record: Record<string, string> = {};
        row.forEach((cell, index) => { const field = fields[index]; if (field && cell !== "") record[field] = cell; });
        // A LEVEL COLUMN MEANS AN EXPORT: only the item's own lines (level 1) are its lines to import.
        if (record.Level != null && record.Level !== "1") { skipped += 1; continue; }
        delete record.Level;
        if (Object.keys(record).length > 0) out.push(record);
    }
    return {rows: out, unmapped, skipped, missing};
}

type Imported = {Row: number; ComponentObjectID?: string | null; Quantity?: any; UnitOfMeasure?: string | null; ReferenceDesignator?: string | null;
                 Configuration?: string | null; Status: "Added" | "Refused"; USID?: string | null; Code?: string | null; Message?: string | null};

function ImportDialog({api, parentUSID, onClose, onImported}: {api: any; parentUSID: string; onClose: () => void; onImported: () => void}) {
    const [text, setText] = useState("");
    const [busy, setBusy] = useState(false);
    const [problem, setProblem] = useState<string | null>(null);
    const [outcome, setOutcome] = useState<{Added: number; Refused: number; rows: Imported[]} | null>(null);
    const parsed = useMemo(() => importRows(text), [text]);
    const ready = parsed.rows.length > 0 && parsed.missing.length === 0 && parsed.rows.length <= 500;

    const choose = (file: File | undefined) => {
        if (!file) return;
        const reader = new FileReader();
        reader.onload = () => setText(String(reader.result ?? ""));
        reader.readAsText(file);
    };
    const send = () => {
        setBusy(true);
        setProblem(null);
        api.post("t1/v1/bom_import", {ItemObjectUSID: parentUSID, Lines: parsed.rows})
            .then((response: any) => {
                const message = response?.data?.PlatformMessage ?? {};
                setOutcome({Added: message.Added ?? 0, Refused: message.Refused ?? 0, rows: listOf<Imported>(message?.ListOfImportedLine?.ImportedLine)});
                if ((message.Added ?? 0) > 0) onImported();
            })
            .catch((failure: any) => setProblem(failure?.response?.data?.Cause ?? "The lines could not be imported."))
            .finally(() => setBusy(false));
    };

    return (
        <Dialog open onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle sx={{fontSize: 16}}>Import lines</DialogTitle>
            <DialogContent sx={{display: "flex", flexDirection: "column", gap: "10px"}}>
                {outcome ? (
                    <>
                        <Typography sx={{fontSize: 13, color: Grey8}}>
                            {outcome.Added} line{outcome.Added === 1 ? "" : "s"} added, {outcome.Refused} refused.
                            {outcome.Refused > 0 ? " The refused rows are listed with the reason; fix them in the sheet and import those rows alone." : ""}
                        </Typography>
                        <Box sx={{display: "grid", gridTemplateColumns: "auto 1.5fr 0.8fr 0.6fr 3fr", gap: "4px 12px", alignItems: "center"}}>
                            {["Row", "Component", "Qty", "", "Outcome"].map(head => (
                                <Typography key={head} sx={{fontSize: 11.5, color: Grey7, textTransform: "uppercase", letterSpacing: "0.04em"}}>{head}</Typography>
                            ))}
                            {outcome.rows.map(row => (
                                <React.Fragment key={row.Row}>
                                    <Typography sx={{fontSize: 12.5, color: Grey7}}>{row.Row}</Typography>
                                    <Typography sx={{fontSize: 12.5, color: Grey8}}>{row.ComponentObjectID ?? "—"}</Typography>
                                    <Typography sx={{fontSize: 12.5, color: Grey8}}>{row.Quantity ?? "—"}{row.UnitOfMeasure ? ` ${row.UnitOfMeasure}` : ""}</Typography>
                                    <Typography sx={{fontSize: 12.5, fontWeight: 600, color: row.Status === "Added" ? "#1B5E20" : "#B3261E"}}>{row.Status}</Typography>
                                    <Typography sx={{fontSize: 12.5, color: row.Status === "Added" ? Grey7 : "#B3261E"}}>
                                        {row.Status === "Added" ? "" : `${row.Code ? `${row.Code}: ` : ""}${row.Message ?? ""}`}
                                    </Typography>
                                </React.Fragment>
                            ))}
                        </Box>
                    </>
                ) : (
                    <>
                        <Typography sx={{fontSize: 12.5, color: Grey7, lineHeight: 1.5}}>
                            Paste rows or choose a CSV. The first row names the columns: Part (the component's ID), Quantity and Unit are
                            required; Designator, Cost, Currency, Configuration (by name) and Pinned are read when present. A file exported from
                            a bill imports as is; only its level 1 rows are taken. Each row goes through the same rules as a typed line and is
                            answered on its own.
                        </Typography>
                        <Box sx={{display: "flex", gap: "12px", alignItems: "center"}}>
                            <Button size="small" variant="outlined" component="label" sx={{textTransform: "none"}}>
                                Choose a file
                                <input hidden type="file" accept=".csv,.tsv,.txt,text/csv,text/tab-separated-values,text/plain"
                                       onChange={e => choose(e.target.files?.[0])}/>
                            </Button>
                            <Typography sx={{fontSize: 12.5, color: Grey7}}>or paste below</Typography>
                        </Box>
                        <TextField multiline minRows={6} maxRows={14} size="small" value={text} onChange={e => setText(e.target.value)}
                                   placeholder={"Part,Quantity,Unit,Designator\nITM-000123,4,EA,B1-B4\nITM-000456,0.5,KG,"}
                                   slotProps={{htmlInput: {"data-testid": "bom-import-text", style: {fontFamily: "ui-monospace, Menlo, monospace", fontSize: 12.5}}}}/>
                        {text.trim() !== "" ? (
                            <Typography sx={{fontSize: 12.5, color: parsed.missing.length > 0 || parsed.rows.length > 500 ? "#B3261E" : Grey7}}>
                                {parsed.missing.length > 0
                                    ? `No column for ${parsed.missing.join(", ")}.`
                                    : `${parsed.rows.length} row${parsed.rows.length === 1 ? "" : "s"} to import${parsed.skipped > 0 ? `; ${parsed.skipped} below level 1 skipped` : ""}${parsed.rows.length > 500 ? "; at most 500 in one import" : ""}.`}
                                {parsed.unmapped.length > 0 ? ` Ignored column${parsed.unmapped.length === 1 ? "" : "s"}: ${parsed.unmapped.join(", ")}.` : ""}
                            </Typography>
                        ) : null}
                    </>
                )}
                {problem ? <Typography sx={{fontSize: 12.5, color: "#B3261E"}}>{problem}</Typography> : null}
            </DialogContent>
            <DialogActions>
                <Button size="small" sx={{textTransform: "none"}} onClick={onClose}>{outcome ? "Close" : "Cancel"}</Button>
                {!outcome ? (
                    <Button size="small" variant="contained" disableElevation sx={{textTransform: "none"}} disabled={!ready || busy} onClick={send}>
                        {busy ? "Importing…" : "Import"}
                    </Button>
                ) : null}
            </DialogActions>
        </Dialog>
    );
}

// ── compare: two states of the bill, line by line (2026-10-31) ───────────────────────────────────
type Difference = {Kind: "Added" | "Removed" | "Changed"; Level: number; Path: string[]; ObjectUSID: string; ObjectID?: string | null; Name?: string | null;
                   ReferenceDesignator?: string | null; Substitute?: boolean; Quantity?: any; UnitOfMeasure?: string | null;
                   ListOfChange?: {Change: {Field: string; Before: any; After: any}[] | {Field: string; Before: any; After: any}}};
type Side = {Kind: "Now" | "Snapshot"; USID?: string | null; Reason?: string | null; ItemDisplayRevision?: string | null; CreatedDatetime?: string | null; Fingerprint?: string};
type Comparison = {Same: boolean; Added: number; Removed: number; Changed: number; Unchanged: number; From: Side; To: Side;
                   ListOfBillOfMaterialsDifference?: {BillOfMaterialsDifference: Difference[] | Difference}};

const CHANGE_LABEL: Record<string, string> = {Quantity: "Quantity", UnitOfMeasure: "Unit", PinnedRevisionUSID: "Pinned revision",
                                              StandardCost: "Std cost", StandardCostCurrency: "Currency", ConfigurationUSID: "Configuration"};

function ComparePanel({api, objectUSID, snapshots}: {api: any; objectUSID: string; snapshots: Snapshot[]}) {
    // THE DEFAULT QUESTION: what changed since the latest capture.
    const [from, setFrom] = useState<string>(snapshots[0]?.USID ?? "");
    const [to, setTo] = useState<string>("");
    const [comparison, setComparison] = useState<Comparison | null>(null);
    const [problem, setProblem] = useState<string | null>(null);
    useEffect(() => {
        if (!from && !to) { setComparison(null); setProblem("Choose a capture on at least one side."); return; }
        setProblem(null);
        const params: any = {ItemObjectUSID: objectUSID};
        if (from) params.FromSnapshotUSID = from;
        if (to) params.ToSnapshotUSID = to;
        api.get("t1/v1/bom_compare", {params})
            .then((response: any) => setComparison(response?.data?.PlatformMessage ?? null))
            .catch((failure: any) => { setComparison(null); setProblem(failure?.response?.data?.Cause ?? "The bills could not be compared."); });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [objectUSID, from, to]);
    const rows = listOf<Difference>(comparison?.ListOfBillOfMaterialsDifference?.BillOfMaterialsDifference);
    const side = (label: string, value: string, set: (value: string) => void) => (
        <TextField select size="small" value={value} sx={{minWidth: 240}} label={label} onChange={e => set(e.target.value)}
                   slotProps={{select: {displayEmpty: true}, inputLabel: {shrink: true}}}>
            <MenuItem value="">Now</MenuItem>
            {snapshots.map(one => <MenuItem key={one.USID} value={one.USID}>{snapshotLabel(one)}</MenuItem>)}
        </TextField>
    );
    const colour = (kind: Difference["Kind"]) => kind === "Added" ? "#1B5E20" : kind === "Removed" ? "#B3261E" : "#7A4B00";
    const detail = (row: Difference) => {
        if (row.Kind !== "Changed") return `${row.Quantity ?? "—"}${row.UnitOfMeasure ? ` ${row.UnitOfMeasure}` : ""}`;
        return listOf(row.ListOfChange?.Change).map(one => `${CHANGE_LABEL[one.Field] ?? one.Field} ${one.Before ?? "—"} → ${one.After ?? "—"}`).join("; ");
    };
    return (
        <Box sx={{marginBottom: "10px"}}>
            <Box sx={{display: "flex", gap: "12px", alignItems: "center", marginBottom: "10px", flexWrap: "wrap"}}>
                {side("From", from, setFrom)}
                {side("To", to, setTo)}
            </Box>
            {problem ? <Typography sx={{fontSize: 12.5, color: "#B3261E"}}>{problem}</Typography> : null}
            {comparison ? (
                <Typography sx={{fontSize: 12.5, color: Grey7, marginBottom: "10px"}} data-testid="bom-compare-summary">
                    {comparison.Same
                        ? "The two bills are the same: their fingerprints match."
                        : `${comparison.Added} added, ${comparison.Removed} removed, ${comparison.Changed} changed, ${comparison.Unchanged} unchanged.`}
                </Typography>
            ) : null}
            {rows.length > 0 ? (
                <Box sx={{display: "grid", gridTemplateColumns: "0.8fr 3fr 1fr 3fr", gap: "4px 12px", alignItems: "center"}}>
                    {["", "Item", "Designator", "Detail"].map((head, index) => (
                        <Typography key={`${head}${index}`} sx={{fontSize: 11.5, color: Grey7, textTransform: "uppercase", letterSpacing: "0.04em"}}>{head}</Typography>
                    ))}
                    {rows.map((row, index) => (
                        <React.Fragment key={`${row.Kind}${row.Path?.join("/")}${row.ReferenceDesignator ?? ""}${index}`}>
                            <Typography sx={{fontSize: 12.5, fontWeight: 600, color: colour(row.Kind)}}>{row.Kind}</Typography>
                            <Typography sx={{fontSize: 12.5, color: Grey8, paddingLeft: `${Math.max(0, (row.Level ?? 1) - 1) * 18}px`}}>
                                {row.ObjectID ? `${row.ObjectID} · ` : ""}{row.Name ?? row.ObjectUSID}{row.Substitute ? " (substitute)" : ""}
                            </Typography>
                            <Typography sx={{fontSize: 12.5, color: Grey8}}>{row.ReferenceDesignator ?? "—"}</Typography>
                            <Typography sx={{fontSize: 12.5, color: Grey8}}>{detail(row)}</Typography>
                        </React.Fragment>
                    ))}
                </Box>
            ) : null}
        </Box>
    );
}
